import { type GetServerSidePropsContext } from 'next';
import { getEntries, type MarketCountryCode } from '@pkgs/api';

import { COOKIE_KEY, type FeatureFlagCookieKey } from '@/consts/cookies';
import type { AppPageProps } from '@/app/[marketCountryCode]/[locale]/_types/appPageProps';

type FeatureFlagCookieName = FeatureFlagCookieKey | `${FeatureFlagCookieKey}-${MarketCountryCode}`;

export const FeatureFlag = {
  SHOW_I18N_KEYS: 'showI18nKeys',
  IS_AI_SEARCH_ENABLED: 'isAiSearchEnabled',
  DEBUG_SSR: 'debugSSR',
  QUERY_DEVTOOLS: 'queryDevtools',
  MAP_DEFAULT_TOGGLED_ON: 'mapDefaultToggledOn',
  SHOW_WHATSAPP_BUTTON: 'showWhatsAppButton',
  IS_SMART_SORT_ENABLED: 'isSmartSortEnabled',
  IS_CHATBOT_ENABLED: 'isChatbotEnabled',
} as const;

export type FeatureFlag = (typeof FeatureFlag)[keyof typeof FeatureFlag];

export type FeatureFlagDetails = {
  countrySpecific?: boolean;
  key: FeatureFlagCookieKey;
  description: string;
  defaultValue: boolean;
};

export const FEATURE_FLAGS: Record<FeatureFlag, FeatureFlagDetails> = {
  [FeatureFlag.SHOW_I18N_KEYS]: {
    description: 'Display untranslated lokalise keys.',
    key: COOKIE_KEY.labelsDisabled,
    defaultValue: false,
  },

  [FeatureFlag.IS_AI_SEARCH_ENABLED]: {
    description: 'Enable AI Search Mode.',
    key: COOKIE_KEY.aiSearchEnabled,
    defaultValue: false,
  },
  [FeatureFlag.DEBUG_SSR]: {
    description: 'Enable SSR debug logging.',
    key: COOKIE_KEY.debugSsr,
    defaultValue: false,
  },
  [FeatureFlag.QUERY_DEVTOOLS]: {
    description: 'Enable query devtools.',
    key: COOKIE_KEY.productionQueryDevtools,
    defaultValue: false,
  },
  [FeatureFlag.MAP_DEFAULT_TOGGLED_ON]: {
    description: 'Map is toggled on by default.',
    key: COOKIE_KEY.mapDefaultToggledOn,
    defaultValue: true,
  },
  [FeatureFlag.SHOW_WHATSAPP_BUTTON]: {
    description: 'Show WhatsApp contact buttons on expose pages.',
    key: COOKIE_KEY.showWhatsAppButton,
    defaultValue: false,
  },
  [FeatureFlag.IS_SMART_SORT_ENABLED]: {
    countrySpecific: true,
    description: 'Enable Smart Sort.',
    key: COOKIE_KEY.smartSortEnabled,
    defaultValue: false,
  },
  [FeatureFlag.IS_CHATBOT_ENABLED]: {
    description: 'Enable Chatbot.',
    key: COOKIE_KEY.chatbotEnabled,
    defaultValue: false,
  },
} as const;

const getFeatureFlagCookieKey = ({
  countryCode,
  featureFlag,
}: {
  countryCode?: MarketCountryCode;
  featureFlag: FeatureFlagDetails;
}): FeatureFlagCookieName => {
  if (!featureFlag.countrySpecific || !countryCode) {
    return featureFlag.key;
  }

  return `${featureFlag.key}-${countryCode}`;
};

const resolveFeatureFlagValue = ({
  cookieValue,
  defaultValue,
  queryValue,
}: {
  cookieValue?: string;
  defaultValue: boolean;
  queryValue?: string | string[];
}): boolean => {
  if (cookieValue === 'true' || queryValue === 'true') {
    return true;
  }

  if (cookieValue === 'false' || queryValue === 'false') {
    return false;
  }

  return defaultValue;
};

export const evaluateFeatureFlags = ({
  cookies,
  query,
  countryCode,
}: {
  cookies: Partial<Record<string, string>>;
  query: Awaited<AppPageProps['searchParams']>;
  countryCode?: MarketCountryCode;
}): Record<FeatureFlag, boolean> => {
  return getEntries(FEATURE_FLAGS).reduce(
    (acc, [flag, details]) => {
      const cookieKey = getFeatureFlagCookieKey({
        countryCode,
        featureFlag: details,
      });

      acc[flag] = resolveFeatureFlagValue({
        cookieValue: cookies[cookieKey],
        defaultValue: details.defaultValue,
        queryValue: query[details.key],
      });

      return acc;
    },
    {} as Record<FeatureFlag, boolean>,
  );
};

// TODO-APP-MIGRATION: Remove this Pages Router wrapper after its callers migrate to the App Router.
export const getFeatureFlags = ({
  context,
  countryCode,
}: {
  context: GetServerSidePropsContext;
  countryCode?: MarketCountryCode;
}): Record<FeatureFlag, boolean> => evaluateFeatureFlags({ cookies: context.req.cookies, query: context.query, countryCode });
